/**
 * Clients Admin Service — SystemAdmin only.
 * Lists every client and lets a SystemAdmin edit the NMVTIS-retention knobs
 * (TimeZone + NmvtisRetentionDays). All other client fields are read-only here.
 */

import { API_ENDPOINTS } from '../config/api';
import { makeAuthenticatedRequest, createApiError } from './apiService';

export interface ClientAdminResponse {
  id: string;
  name: string;
  description: string;
  streetAddress: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  country: string | null;
  phone: string | null;
  requireMfa: boolean;
  passwordExpirationDays: number | null;
  enableApiKeyAccess: boolean;
  timeZone: string;
  nmvtisRetentionDays: number | null; // null = the global default applies
  nmvtisRetentionMaxDays: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  // Onboarding contact + billing. The bank account number is only ever returned as last-4.
  contactName: string | null;
  contactEmail: string | null;
  autoPay: boolean;
  invoiceFrequency: InvoiceFrequency;
  autoInvoicingEnabled: boolean;
  /** Whether this client bills on graduated volume tiers rather than flat pricing. */
  tieredPricingEnabled: boolean;
  invoiceTermsId?: string | null;
  bankName: string | null;
  bankAccountHolderName: string | null;
  bankRoutingNumber: string | null;
  bankAccountNumberLast4: string | null;
  bankAccountType: BankAccountType | null;
  netSuiteCustomerId?: string;
  mvrDppaPermissibleUse?: string | null;
  nmvtisCustomerCategory?: string | null;
}

// Mirror of UpdateClientAdminRequest on the backend — every field optional so
// the page only sends what actually changed. Security policies (MFA / password /
// API key access) may be set by a SystemAdmin on behalf of the org.
export interface UpdateClientAdminRequest {
  name?: string;
  description?: string;
  isActive?: boolean;
  streetAddress?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  country?: string | null;
  phone?: string | null;
  timeZone?: string;
  nmvtisRetentionDays?: number; // SystemAdmin only; 0 clears it so the global default applies
  // Onboarding contact + billing. Send bankAccountNumber only to change it (raw value,
  // encrypted server-side); omit to leave the stored number unchanged.
  contactName?: string | null;
  contactEmail?: string | null;
  autoPay?: boolean;
  invoiceFrequency?: InvoiceFrequency;
  autoInvoicingEnabled?: boolean;
  /** Whether this client bills on graduated volume tiers rather than flat pricing. */
  tieredPricingEnabled?: boolean;
  invoiceTermsId?: string | null;
  bankName?: string | null;
  bankAccountHolderName?: string | null;
  bankRoutingNumber?: string | null;
  bankAccountNumber?: string | null;
  bankAccountType?: BankAccountType | null;
  // NetSuite linkage — pass null to clear, omit to leave unchanged
  netSuiteCustomerId?: string | null;
  // Security policies
  requireMfa?: boolean;
  passwordExpirationDays?: number;
  enableApiKeyAccess?: boolean;
  // Compliance
  mvrDppaPermissibleUse?: string | null;
  nmvtisCustomerCategory?: string | null;
}

// Enum values match the backend (no string-enum converter → numeric).
export enum InvoiceFrequency { Monthly = 0, BiWeekly = 1 }
export enum BankAccountType { Checking = 0, Savings = 1 }

// A company awaiting approval ("Get Started" submitted, not yet activated). No user account
// exists yet — only the contact who submitted the request.
export interface PendingCompanyResponse {
  clientId: string;
  companyName: string;
  description: string | null;
  streetAddress: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  country: string | null;
  phone: string | null;
  createdAt: string;
  contactName: string | null;
  contactEmail: string | null;
  netSuiteCustomerId?: string;
}

export interface SetupActivateUserInvite {
  email: string;
  firstName: string;
  lastName: string;
  role: 'ClientAdmin' | 'ClientUser';
}

export interface SetupActivateRequest {
  name?: string;
  description?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  phone?: string;
  autoPay?: boolean;
  invoiceFrequency?: InvoiceFrequency;
  bankName?: string;
  bankAccountHolderName?: string;
  bankRoutingNumber?: string;
  bankAccountNumber?: string;
  bankAccountType?: BankAccountType;
  users: SetupActivateUserInvite[];
}

export const listPendingCompanies = async (): Promise<PendingCompanyResponse[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.PENDING);
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

// SystemAdmin creates a pending company directly (call-in client). Returns the new
// pending company, ready to feed straight into the Setup & Activate flow.
export interface CreatePendingClientRequest {
  companyName: string;
  contactName?: string;
  contactEmail?: string;
  phone?: string;
  description?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  netSuiteCustomerId?: string;
}

export const createPendingClientAdmin = async (payload: CreatePendingClientRequest): Promise<PendingCompanyResponse> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.CREATE_PENDING, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export const setupAndActivateCompany = async (id: string, payload: SetupActivateRequest): Promise<void> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.SETUP_ACTIVATE(id), {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw await createApiError(response);
};

export const rejectCompany = async (id: string): Promise<void> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.REJECT(id), { method: 'POST' });
  if (!response.ok) throw await createApiError(response);
};

export const listClientsAdmin = async (): Promise<ClientAdminResponse[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.LIST);
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export const getClientAdmin = async (id: string): Promise<ClientAdminResponse> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.GET(id));
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export const updateClientAdmin = async (
  id: string,
  request: UpdateClientAdminRequest,
): Promise<ClientAdminResponse> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.UPDATE(id), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export interface AdminInviteUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  role: 'ClientUser' | 'ClientAdmin';
}

export const inviteUserAdmin = async (clientId: string, payload: AdminInviteUserRequest): Promise<void> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.INVITE_USER(clientId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw await createApiError(response);
};

// Permanently deletes an organization and its data (SystemAdmin). Irreversible.
export const deleteClientAdmin = async (id: string): Promise<void> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.DELETE(id), {
    method: 'DELETE',
  });
  if (!response.ok) throw await createApiError(response);
};

/**
 * IANA timezone names used for the admin TimeZone picker. Covers the 50 US states +
 * DC + US territories. Anything beyond this list can still be saved through the
 * raw API but the dropdown stays focused on what AAS actually serves.
 */
export const IANA_TIMEZONES: { value: string; label: string }[] = [
  { value: 'America/New_York', label: 'Eastern — America/New_York' },
  { value: 'America/Detroit', label: 'Eastern (MI) — America/Detroit' },
  { value: 'America/Indiana/Indianapolis', label: 'Eastern (IN) — America/Indiana/Indianapolis' },
  { value: 'America/Chicago', label: 'Central — America/Chicago' },
  { value: 'America/Denver', label: 'Mountain — America/Denver' },
  { value: 'America/Boise', label: 'Mountain (ID) — America/Boise' },
  { value: 'America/Phoenix', label: 'Arizona (no DST) — America/Phoenix' },
  { value: 'America/Los_Angeles', label: 'Pacific — America/Los_Angeles' },
  { value: 'America/Anchorage', label: 'Alaska — America/Anchorage' },
  { value: 'America/Juneau', label: 'Alaska (Juneau) — America/Juneau' },
  { value: 'Pacific/Honolulu', label: 'Hawaii — Pacific/Honolulu' },
  { value: 'America/Puerto_Rico', label: 'Atlantic (PR/VI) — America/Puerto_Rico' },
  { value: 'America/St_Thomas', label: 'Atlantic (USVI) — America/St_Thomas' },
  { value: 'Pacific/Guam', label: 'Chamorro (Guam) — Pacific/Guam' },
  { value: 'Pacific/Saipan', label: 'Chamorro (CNMI) — Pacific/Saipan' },
  { value: 'Pacific/Pago_Pago', label: 'Samoa — Pacific/Pago_Pago' },
];

// ---- SystemAdmin: manage an org's users + API keys ----

export interface AdminClientUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isActive: boolean;
  isEmailVerified: boolean;
  canAccessNmvtis: boolean;
  canAccessTheftLien: boolean;
  canAccessMotorVehicleRecord: boolean;
  canAccessVinDecoder: boolean;
  canAccessAstsPublic: boolean;
  canAccessInvoicing: boolean;
  canManageClients: boolean;
  mfaEnabled: boolean;
  isSystemAdmin: boolean;
  receivePortalAlerts: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  markedForDeletionAt: string | null;
}

export interface AdminApiKey {
  id: string;
  keyPreview: string;
  name: string;
  keyType: string;
  ownerName: string;
  ownerEmail: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  isActive: boolean;
}

export const listClientUsersAdmin = async (clientId: string): Promise<AdminClientUser[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.USERS(clientId));
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export const toggleClientUserActive = async (userId: string): Promise<void> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.USER_TOGGLE_ACTIVE(userId), { method: 'POST' });
  if (!response.ok) throw await createApiError(response);
};

export const removeClientUser = async (userId: string): Promise<void> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.USER_REMOVE(userId), { method: 'POST' });
  if (!response.ok) throw await createApiError(response);
};

// Grant/revoke a user's Invoicing (NetSuite invoice menu) access. SystemAdmin only.
export const setClientUserInvoicingAccess = async (userId: string, enabled: boolean): Promise<void> => {
  const url = `${API_ENDPOINTS.CLIENTS_ADMIN.USER_INVOICING_ACCESS(userId)}?enabled=${enabled}`;
  const response = await makeAuthenticatedRequest(url, { method: 'POST' });
  if (!response.ok) throw await createApiError(response);
};

// Grant/revoke a user's Client management (onboarding) access. SystemAdmin only.
export const setClientUserClientAdminAccess = async (userId: string, enabled: boolean): Promise<void> => {
  const url = `${API_ENDPOINTS.CLIENTS_ADMIN.USER_CLIENT_ADMIN_ACCESS(userId)}?enabled=${enabled}`;
  const response = await makeAuthenticatedRequest(url, { method: 'POST' });
  if (!response.ok) throw await createApiError(response);
};

export interface AdminUserPermissions {
  canAccessNmvtis: boolean;
  canAccessTheftLien: boolean;
  canAccessMotorVehicleRecord: boolean;
  canAccessVinDecoder: boolean;
  canAccessAstsPublic: boolean;
  mfaEnabled?: boolean;
  receivePortalAlerts?: boolean;
}

// Update a user's section permissions. SystemAdmin only (uses PUT /api/users/{id}/permissions).
export const setClientUserPermissions = async (userId: string, permissions: AdminUserPermissions): Promise<void> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.USER_MANAGEMENT.UPDATE_PERMISSIONS(userId), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(permissions),
  });
  if (!response.ok) throw await createApiError(response);
};

// Change a user's role within the org. role: 'ClientUser' | 'ClientAdmin'.
export const changeClientUserRole = async (userId: string, role: string): Promise<void> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.USER_ROLE(userId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
  if (!response.ok) throw await createApiError(response);
};

export const listClientApiKeysAdmin = async (clientId: string): Promise<AdminApiKey[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.API_KEYS(clientId));
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export const revokeClientApiKey = async (keyId: string): Promise<void> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.API_KEY_REVOKE(keyId), { method: 'DELETE' });
  if (!response.ok) throw await createApiError(response);
};

// Full secret returned ONCE — display it immediately, it cannot be retrieved later.
export interface CreatedApiKey {
  apiKey: string;
  name: string;
  createdAt: string;
}

export const createClientApiKey = async (clientId: string, name: string): Promise<CreatedApiKey> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.API_KEY_CREATE(clientId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

// ---- SystemAdmin: per-org, per-state pricing (state DMV / MVR lookups) ----

export interface ClientStatePrice {
  stateCode: string;
  stateName: string;
  standardBasePrice: number;
  orgPrice: number | null;       // null = uses the standard base
  discountPercent: number | null; // 0–100
  isActive: boolean;
  mvrEnabled: boolean;
  hasOverride: boolean;
  effectivePrice: number;        // what a lookup actually costs this org in this state
}

export interface StatePriceItem {
  stateCode: string;
  orgPrice?: number | null;
  discountPercent?: number | null;
  isActive: boolean;
  mvrEnabled: boolean;
}

export const listClientStatePrices = async (clientId: string): Promise<ClientStatePrice[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.STATE_PRICES(clientId));
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

// Full-set replace — send every state that has a custom price or discount.
export const saveClientStatePrices = async (clientId: string, items: StatePriceItem[]): Promise<ClientStatePrice[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.STATE_PRICES(clientId), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

// ---- SystemAdmin: per-org pricing per service offering (NMVTIS, VIN Decode, NVS, MVR) ----

export interface ClientServicePrice {
  serviceType: string;
  label: string;
  description: string;
  standardBasePrice: number;
  orgPrice: number | null;        // null = uses the standard base
  discountPercent: number | null; // 0–100
  isActive: boolean;
  hasOverride: boolean;
  effectivePrice: number;
}

export interface ServicePriceItem {
  serviceType: string;
  orgPrice?: number | null;
  discountPercent?: number | null;
  isActive: boolean;
}

export const listClientServicePrices = async (clientId: string): Promise<ClientServicePrice[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.SERVICE_PRICES(clientId));
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

// Full-set replace — send every offering that has a custom price or discount.
export const saveClientServicePrices = async (clientId: string, items: ServicePriceItem[]): Promise<ClientServicePrice[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.SERVICE_PRICES(clientId), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export interface PendingInvitation {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  status: 'Pending' | 'Accepted';
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  lastLoginAt: string | null;
}

export interface InvitationLinkResult {
  invitationId: string;
  email: string;
  link: string;
  expiresAt: string;
}

export const listClientInvitationsAdmin = async (clientId: string): Promise<PendingInvitation[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.INVITATIONS(clientId));
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export const getInvitationLink = async (invitationId: string): Promise<InvitationLinkResult> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.INVITATION_LINK(invitationId), { method: 'POST' });
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export const resendInvitationAdmin = async (invitationId: string): Promise<{ invitationId: string; email: string; expiresAt: string }> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.INVITATION_RESEND(invitationId), { method: 'POST' });
  if (!response.ok) throw await createApiError(response);
  return response.json();
};
